import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import InvoiceUploader from "./InvoiceUploader";
import { COUPON_CONFIG } from "../constants/couponConstants";
import { useMutation, useLazyQuery } from "@apollo/client";
import {
  GENERATE_COUPON_NUMBER,
  SUBMIT_COUPON_CLAIM,
  CHECK_COUPON_CLAIMED,
  SEND_COUPON_EMAIL,
} from "../graphql/queries";

const CouponFormDialog = ({ open, onClose, scannedData, scannedRole, onSuccess }) => {
  const [couponNo, setCouponNo] = useState("");
  const [totalBill, setTotalBill] = useState("");
  const [discountApplied, setDiscountApplied] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceFileName, setInvoiceFileName] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [generateCouponNumber] = useMutation(GENERATE_COUPON_NUMBER);
  const [submitCouponClaim] = useMutation(SUBMIT_COUPON_CLAIM);
  const [checkCouponClaimedQuery] = useLazyQuery(CHECK_COUPON_CLAIMED);
  const [sendCouponEmail] = useMutation(SEND_COUPON_EMAIL);

  // Generate coupon number on mount
  useEffect(() => {
    if (open && scannedData) {
      generateCouponNumber()
        .then((result) => {
          if (result.data?.generateCouponNumber) {
            setCouponNo(result.data.generateCouponNumber);
          }
        })
        .catch((error) => {
          console.error("Error generating coupon number:", error);
        });
    }
  }, [open, scannedData, generateCouponNumber]);

  // Calculate discount and grand total when total bill changes
  useEffect(() => {
    const bill = parseFloat(totalBill) || 0;
    
    if (bill < COUPON_CONFIG.MIN_PURCHASE_AMOUNT) {
      setDiscountApplied(0);
      setGrandTotal(bill);
    } else {
      const calculatedDiscount = (bill * COUPON_CONFIG.DISCOUNT_PERCENTAGE) / 100;
      const finalDiscount = Math.min(calculatedDiscount, COUPON_CONFIG.MAX_DISCOUNT_AMOUNT);
      setDiscountApplied(finalDiscount);
      setGrandTotal(bill - finalDiscount);
    }
  }, [totalBill]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!totalBill || parseFloat(totalBill) <= 0) {
      newErrors.totalBill = "Total bill is required and must be greater than 0";
    } else if (parseFloat(totalBill) < COUPON_CONFIG.MIN_PURCHASE_AMOUNT) {
      newErrors.totalBill = `Minimum purchase amount is ${COUPON_CONFIG.MIN_PURCHASE_AMOUNT} OMR`;
    }

    const bill = parseFloat(totalBill) || 0;
    if (bill >= COUPON_CONFIG.MIN_PURCHASE_AMOUNT && !invoiceData) {
      newErrors.invoice = "Invoice upload is mandatory";
    }

    if (!termsAccepted) {
      newErrors.terms = "You must accept the terms and conditions";
    }

    // Validate validity period
    const now = new Date();
    const startDate = new Date(COUPON_CONFIG.VALIDITY_START_DATE);
    const endDate = new Date(COUPON_CONFIG.VALIDITY_END_DATE);

    if (now < startDate) {
      newErrors.validity = "Coupon validity period has not started yet";
    } else if (now > endDate) {
      newErrors.validity = "Coupon validity period has expired";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const LOG = (step, detail) => console.log("[CouponSubmit]", step, detail !== undefined ? detail : "");

    LOG("1. handleSubmit called", { scannedData, scannedRole, hasInvoiceData: !!invoiceData, totalBill, termsAccepted });

    if (!scannedData || !scannedRole) {
      LOG("1b. BLOCKED: missing scan data");
      setErrors({ general: "Missing scan data. Please scan again and open the coupon form." });
      return;
    }
    LOG("2. scan data OK");

    if (!validateForm()) {
      LOG("2b. BLOCKED: validation failed (check form errors above)");
      setErrors((prev) => ({
        ...prev,
        general: "Please fix the errors below and try again.",
      }));
      return;
    }
    LOG("3. validation OK");

    setLoading(true);
    setErrors((prev) => ({ ...prev, general: null }));

    try {
      LOG("4. calling checkCouponClaimed...");
      const checkResult = await checkCouponClaimedQuery({
        variables: {
          id: scannedData,
          role: scannedRole,
        },
      });
      LOG("5. checkCouponClaimed result", checkResult?.data);

      if (checkResult.data?.checkCouponClaimed) {
        LOG("5b. BLOCKED: already claimed");
        setErrors({ general: "This coupon has already been claimed by this user" });
        setLoading(false);
        return;
      }
      LOG("6. not already claimed, calling submitCouponClaim...");

      const result = await submitCouponClaim({
        variables: {
          id: scannedData,
          role: scannedRole,
          couponNo,
          totalBill: parseFloat(totalBill),
          discountApplied,
          grandTotal,
          invoiceData,
          invoiceFileName: invoiceFileName || "invoice.jpg",
        },
      });
      LOG("7. submitCouponClaim result", { hasData: !!result?.data, hasError: !!result?.errors, data: result?.data });

      if (result.data?.submitCouponClaim) {
        LOG("8. success, sending email...");
        try {
          await sendCouponEmail({
            variables: {
              userId: scannedData,
              role: scannedRole,
              couponNo,
              totalBill: parseFloat(totalBill),
              discountApplied,
              grandTotal,
            },
          }).catch((err) => {
            console.error("Error sending coupon email:", err);
          });
        } catch (error) {
          console.error("Error in email sending:", error);
        }

        LOG("9. done, closing");
        onSuccess?.(result.data.submitCouponClaim);
        handleClose();
      } else {
        LOG("7b. no success payload", result?.errors || result);
        setErrors({ general: (result?.errors?.[0]?.message) || "Claim submitted but no success message" });
      }
    } catch (error) {
      LOG("ERROR", error?.message || error);
      console.error("Error submitting coupon claim:", error);
      setErrors({ general: error.message || "Failed to submit coupon claim" });
    } finally {
      setLoading(false);
      LOG("10. finally (loading off)");
    }
  };

  const handleClose = () => {
    setCouponNo("");
    setTotalBill("");
    setDiscountApplied(0);
    setGrandTotal(0);
    setInvoiceData(null);
    setInvoiceFileName(null);
    setTermsAccepted(false);
    setErrors({});
    onClose();
  };

  const handleInvoiceChange = (data, fileName) => {
    setInvoiceData(data);
    setInvoiceFileName(fileName);
    if (errors.invoice) {
      setErrors({ ...errors, invoice: null });
    }
  };

  const minMet = parseFloat(totalBill || 0) >= COUPON_CONFIG.MIN_PURCHASE_AMOUNT;
  const canSubmit = !!(termsAccepted && totalBill && parseFloat(totalBill) > 0 && (minMet ? invoiceData : false));
  const isSubmitDisabled = loading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEnforceFocus>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Coupon Claim Form</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        {errors.validity && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.validity}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Scanned ID: {scannedData}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Role: {scannedRole}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Coupon No"
          value={couponNo}
          margin="normal"
          InputProps={{
            readOnly: true,
          }}
          helperText="Auto-generated coupon code"
        />

        <TextField
          fullWidth
          label="Total Bill (OMR)"
          type="number"
          value={totalBill}
          onChange={(e) => {
            setTotalBill(e.target.value);
            if (errors.totalBill) {
              setErrors({ ...errors, totalBill: null });
            }
          }}
          margin="normal"
          error={!!errors.totalBill}
          helperText={errors.totalBill || `Minimum purchase: ${COUPON_CONFIG.MIN_PURCHASE_AMOUNT} OMR`}
          inputProps={{ min: 0, step: 0.01 }}
          required
        />

        <TextField
          fullWidth
          label="Discount Applied (OMR)"
          value={discountApplied.toFixed(2)}
          margin="normal"
          InputProps={{
            readOnly: true,
          }}
          helperText={`${COUPON_CONFIG.DISCOUNT_PERCENTAGE}% discount (Max: ${COUPON_CONFIG.MAX_DISCOUNT_AMOUNT} OMR)`}
        />

        <TextField
          fullWidth
          label="Grand Total (OMR)"
          value={grandTotal.toFixed(2)}
          margin="normal"
          InputProps={{
            readOnly: true,
          }}
          helperText="Total bill - Discount applied"
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Invoice *
          </Typography>
          <InvoiceUploader
            value={invoiceData}
            onChange={handleInvoiceChange}
            error={!!errors.invoice}
            helperText={errors.invoice || (parseFloat(totalBill || 0) >= COUPON_CONFIG.MIN_PURCHASE_AMOUNT ? "Upload invoice or capture image (Mandatory)" : "Enter total bill ≥ " + COUPON_CONFIG.MIN_PURCHASE_AMOUNT + " OMR to enable upload")}
            disabled={parseFloat(totalBill || 0) < COUPON_CONFIG.MIN_PURCHASE_AMOUNT}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Discount: {COUPON_CONFIG.DISCOUNT_PERCENTAGE}%</li>
              <li>Validity: {COUPON_CONFIG.VALIDITY_DAYS} days from {COUPON_CONFIG.VALIDITY_START_DATE} to {COUPON_CONFIG.VALIDITY_END_DATE}</li>
              <li>Minimum purchase order: {COUPON_CONFIG.MIN_PURCHASE_AMOUNT} OMR</li>
              <li>Maximum discount value: {COUPON_CONFIG.MAX_DISCOUNT_AMOUNT} OMR</li>
              <li>One-time use only per user</li>
            </ul>
          </Typography>
        </Paper>

        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (errors.terms) {
                  setErrors({ ...errors, terms: null });
                }
              }}
            />
          }
          label="I accept the terms and conditions"
        />
        {errors.terms && (
          <Typography variant="caption" color="error" sx={{ display: "block", ml: 4 }}>
            {errors.terms}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", alignItems: "stretch" }}>
        {!canSubmit && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            To submit: enter total bill (min {COUPON_CONFIG.MIN_PURCHASE_AMOUNT} OMR), upload invoice, and accept terms.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isSubmitDisabled}
          >
            {loading ? "Submitting..." : "Submit Coupon Claim"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CouponFormDialog;
